<section class="py-24 bg-[#0a0a0f] text-white overflow-hidden relative">
	<!-- Background Elements -->
	<div
		class="absolute top-0 right-0 w-[600px] h-[600px] bg-red-500/5 blur-[120px] rounded-full pointer-events-none"
	></div>
	<div
		class="absolute bottom-0 left-0 w-[600px] h-[600px] bg-neon-cyan/5 blur-[120px] rounded-full pointer-events-none"
	></div>

	<div class="container mx-auto px-6 max-w-7xl relative z-10">
		<div class="text-center mb-16">
			<h2 class="text-4xl md:text-5xl font-bold mb-6">
				The Operating System <span class="text-red-500">Challenge</span>
			</h2>
			<p class="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
				Building and maintaining complete systems is complex, expensive,
				and slow.
			</p>
		</div>

		<div class="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
			<!-- Traditional Approach -->
			<div class="relative group">
				<div
					class="absolute inset-0 bg-red-500/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
				></div>
				<div
					class="relative h-full bg-[#13131f] rounded-2xl p-8 border border-red-500/20 hover:border-red-500/50 transition-colors"
				>
					<h3
						class="text-2xl font-bold text-red-500 mb-6 flex items-center gap-3"
					>
						<span class="text-xl">⚠️</span> Traditional Approach
					</h3>
					<ul class="space-y-4">
						<li class="flex items-start gap-3">
							<span class="text-red-500 mt-1">✕</span>
							<span
								class="text-gray-400 group-hover:text-gray-300 transition-colors"
								>Months of requirements gathering</span
							>
						</li>
						<li class="flex items-start gap-3">
							<span class="text-red-500 mt-1">✕</span>
							<span
								class="text-gray-400 group-hover:text-gray-300 transition-colors"
								>Hiring specialized teams ($100K+/engineer)</span
							>
						</li>
						<li class="flex items-start gap-3">
							<span class="text-red-500 mt-1">✕</span>
							<span
								class="text-gray-400 group-hover:text-gray-300 transition-colors"
								>6-12 month delivery timelines</span
							>
						</li>
						<li class="flex items-start gap-3">
							<span class="text-red-500 mt-1">✕</span>
							<span
								class="text-gray-400 group-hover:text-gray-300 transition-colors"
								>Mounting technical debt & security risks</span
							>
						</li>
						<li class="flex items-start gap-3">
							<span class="text-red-500 mt-1">✕</span>
							<span
								class="text-gray-400 group-hover:text-gray-300 transition-colors"
								>Expensive ongoing maintenance</span
							>
						</li>
						<li class="flex items-start gap-3">
							<span class="text-red-500 mt-1">✕</span>
							<span
								class="text-gray-400 group-hover:text-gray-300 transition-colors"
								>Difficult to scale or pivot</span
							>
						</li>
					</ul>
				</div>
			</div>

			<!-- OS Studio Approach -->
			<div class="relative group">
				<div
					class="absolute inset-0 bg-neon-cyan/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
				></div>
				<div
					class="relative h-full bg-[#13131f] rounded-2xl p-8 border border-neon-cyan/20 hover:border-neon-cyan/50 transition-colors"
				>
					<div
						class="absolute inset-0 bg-gradient-to-br from-neon-cyan/5 to-transparent rounded-2xl opacity-50"
					></div>
					<h3
						class="relative text-2xl font-bold text-neon-cyan mb-6 flex items-center gap-3"
					>
						<span class="text-xl">⚡</span> OS Studio Approach
					</h3>
					<ul class="space-y-4 relative z-10">
						<li class="flex items-start gap-3">
							<span class="text-neon-cyan mt-1">✓</span>
							<span class="text-white font-medium"
								>Conversational requirements (hours, not months)</span
							>
						</li>
						<li class="flex items-start gap-3">
							<span class="text-neon-cyan mt-1">✓</span>
							<span class="text-white font-medium"
								>No hiring needed—AI does the building</span
							>
						</li>
						<li class="flex items-start gap-3">
							<span class="text-neon-cyan mt-1">✓</span>
							<span class="text-white font-medium"
								>Live in production within days</span
							>
						</li>
						<li class="flex items-start gap-3">
							<span class="text-neon-cyan mt-1">✓</span>
							<span class="text-white font-medium"
								>Built-in security (SOC2/HIPAA ready)</span
							>
						</li>
						<li class="flex items-start gap-3">
							<span class="text-neon-cyan mt-1">✓</span>
							<span class="text-white font-medium"
								>24/7 automated operations & healing</span
							>
						</li>
						<li class="flex items-start gap-3">
							<span class="text-neon-cyan mt-1">✓</span>
							<span class="text-white font-medium"
								>Instant iteration and infinite scaling</span
							>
						</li>
					</ul>
				</div>
			</div>
		</div>

		<div
			class="mt-16 p-8 relative rounded-2xl overflow-hidden border border-primary/30 bg-primary/5"
		>
			<div
				class="absolute inset-0 bg-gradient-to-r from-primary/10 via-purple-900/20 to-primary/10 blur-xl"
			></div>
			<p
				class="relative text-center text-lg md:text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed"
			>
				<span class="font-bold text-white block mb-2 text-2xl"
					>The Fundamental Shift</span
				>
				Businesses need complete operating systems to stay competitive.
				<br class="hidden md:block" />
				<span class="text-primary-300 font-semibold">
					OS Studio eliminates the bottleneck by using AI to design,
					build, and operate your system automatically.
				</span>
			</p>
		</div>
	</div>
</section>
